// src/features/records/services/attachments/queries.js

import { auth, db } from '../../../../firebase';
import {
  collection,
  documentId,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  limit
} from 'firebase/firestore';
import { requireUser } from '../utils/auth';
import {
  getAttachmentCacheEntry,
  setAttachmentCacheEntry,
  startAttachmentListener
} from './state';

function normalizeMediaTypes(mediaTypes) {
  if (!Array.isArray(mediaTypes) || mediaTypes.length === 0) {
    return null;
  }

  const normalized = Array.from(
    new Set(
      mediaTypes
        .map((type) => (typeof type === 'string' ? type.trim() : ''))
        .filter(Boolean)
    )
  );

  if (!normalized.length) {
    return null;
  }

  return normalized.slice(0, 10);
}

function buildAttachmentQuery(recordId, { mediaTypes = null, pageSize = 30, cursor = null } = {}) {
  const attachmentsRef = collection(db, 'records', recordId, 'attachments');
  const clauses = [];

  const normalizedTypes = normalizeMediaTypes(mediaTypes);
  if (normalizedTypes?.length) {
    if (normalizedTypes.length === 1) {
      clauses.push(where('type', '==', normalizedTypes[0]));
    } else {
      clauses.push(where('type', 'in', normalizedTypes));
    }
  }

  clauses.push(orderBy('uploadedAt', 'desc'));
  clauses.push(orderBy(documentId(), 'desc'));

  if (cursor) {
    const hasId = typeof cursor.id === 'string' && cursor.id.length > 0;
    const hasUploadedAt = Object.prototype.hasOwnProperty.call(cursor, 'uploadedAt');

    if (!hasId || !hasUploadedAt) {
      throw new Error(
        'Attachment pagination cursor must include both `id` and `uploadedAt` to match query ordering.'
      );
    }

    clauses.push(startAfter(cursor.uploadedAt, cursor.id));
  }

  const limitCount = Math.max(1, pageSize || 1);
  clauses.push(limit(limitCount));

  return {
    query: query(attachmentsRef, ...clauses),
    limitCount,
    normalizedTypes
  };
}

function mapAttachmentDoc(recordId, doc) {
  const data = doc.data();
  if (!data) {
    return null;
  }

  return {
    id: doc.id,
    ...data,
    key: `${recordId}/${doc.id}`,
    recordId
  };
}

function buildAttachmentCursor(doc) {
  if (!doc) {
    return null;
  }

  const data = typeof doc.data === 'function' ? doc.data() : null;

  return {
    id: doc.id,
    uploadedAt: data?.uploadedAt ?? null
  };
}

/**
 * Get all attachments for a record with version-aware caching.
 */
export async function getRecordAttachments(recordId, expectedVersion = 0, realtime = false) {
  requireUser();

  const cached = getAttachmentCacheEntry(recordId);
  if (cached && cached.version === expectedVersion && !realtime) {
    return cached.data;
  }

  try {
    if (realtime) {
      return startAttachmentListener(recordId, expectedVersion);
    }

    const attachmentsRef = collection(db, 'records', recordId, 'attachments');
    const q = query(attachmentsRef, orderBy('uploadedAt', 'desc'));

    const snapshot = await getDocs(q);
    const attachments = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      attachments.push({
        id: doc.id,
        ...data
      });
    });

    setAttachmentCacheEntry(recordId, {
      version: expectedVersion,
      data: attachments
    });

    return attachments;
  } catch (error) {
    console.error('Error fetching attachments:', error);
    throw error;
  }
}

/**
 * Subscribe to the attachments subcollection with real-time updates.
 */
export function subscribeToAttachments(recordId, onData, onError) {
  if (!auth.currentUser || !recordId) {
    console.error('subscribeToAttachments: requires auth and recordId');
    return () => {};
  }

  const attachmentsRef = collection(db, 'records', recordId, 'attachments');
  const q = query(attachmentsRef, orderBy('uploadedAt', 'desc'));

  return onSnapshot(
    q,
    (qs) => {
      const attachments = qs.docs.map((d) => ({ id: d.id, ...d.data() }));
      onData(attachments);
    },
    (err) => {
      console.error('Error in attachments subscription:', err);
      onError?.(err);
    }
  );
}

/**
 * Subscribe to a record's attachments with gallery-aware pagination.
 */
export function subscribeRecordAttachments(
  { recordId, mediaTypes = null, limit: pageSize = 30, cursor = null } = {},
  onData,
  onError
) {
  if (!auth.currentUser) {
    const error = new Error('User must be authenticated');
    onError?.(error);
    return () => {};
  }

  if (!recordId) {
    const error = new Error('recordId is required to subscribe to attachments');
    onError?.(error);
    return () => {};
  }

  try {
    const { query: attachmentQuery, limitCount, normalizedTypes } = buildAttachmentQuery(recordId, {
      mediaTypes,
      pageSize,
      cursor
    });

    return onSnapshot(
      attachmentQuery,
      (snapshot) => {
        const attachments = snapshot.docs
          .map((docSnap) => mapAttachmentDoc(recordId, docSnap))
          .filter((attachment) => {
            if (!attachment) return false;
            if (normalizedTypes?.length) {
              return normalizedTypes.includes(attachment.type);
            }
            return true;
          });

        const lastDoc = snapshot.docs[snapshot.docs.length - 1] ?? null;

        onData?.({
          recordId,
          attachments,
          cursor: buildAttachmentCursor(lastDoc),
          size: snapshot.size,
          hasMore: snapshot.size === limitCount
        });
      },
      (error) => {
        console.error('Error subscribing to record attachments:', error);
        onError?.(error);
      }
    );
  } catch (error) {
    console.error('Failed to subscribe to record attachments:', error);
    onError?.(error);
    return () => {};
  }
}

/**
 * Fetch a page of attachments for a record with gallery-aware pagination.
 */
export async function fetchRecordAttachmentsPage(
  { recordId, mediaTypes = null, limit: pageSize = 30, cursor = null } = {}
) {
  requireUser();

  if (!recordId) {
    return {
      recordId,
      attachments: [],
      cursor: null,
      hasMore: false
    };
  }

  try {
    const { query: attachmentQuery, limitCount, normalizedTypes } = buildAttachmentQuery(recordId, {
      mediaTypes,
      pageSize,
      cursor
    });

    const snapshot = await getDocs(attachmentQuery);
    const attachments = snapshot.docs
      .map((docSnap) => mapAttachmentDoc(recordId, docSnap))
      .filter((attachment) => {
        if (!attachment) return false;
        if (normalizedTypes?.length) {
          return normalizedTypes.includes(attachment.type);
        }
        return true;
      });

    const lastDoc = snapshot.docs[snapshot.docs.length - 1] ?? null;

    return {
      recordId,
      attachments,
      cursor: buildAttachmentCursor(lastDoc),
      hasMore: snapshot.size === limitCount
    };
  } catch (error) {
    console.error('Error fetching record attachments page:', error);
    throw error;
  }
}
